import * as F from './fixed'
import type { Fixed } from './fixed'
import * as T from './table'
import * as V from './vec'
import type { Vec } from './vec'

/**
 * Simulação determinística da mesa.
 *
 * DECISÃO CENTRAL — colisão contínua, não discreta. Uma bola a 12 m/s percorre
 * 5cm num passo de 1/240s, quase o dobro do próprio diâmetro: com detecção por
 * sobreposição ela atravessaria a outra sem tocar. Aqui, cada passo calcula o
 * instante exato do primeiro contato, avança até ele, resolve, e continua com
 * o tempo que sobrou. Nenhuma bola atravessa outra, em nenhuma velocidade.
 *
 * Nada de `Math.random` nem `Date`: a mesma entrada produz a mesma saída em
 * qualquer máquina, hoje ou daqui a anos.
 */

export type Ball = {
  id: number
  position: Vec
  velocity: Vec
  /**
   * Rotação da bola, guardada como VELOCIDADE DE SUPERFÍCIE: `s = R·ω`, em m/s.
   *
   * Guardar `R·ω` em vez de `ω` não é detalhe de estilo — é o que torna a v3
   * possível neste motor. O ponto fixo é Q16.16 com invariante |x| < 256, e
   * `ω = v/R` com R = 0,0286 chega a 420 rad/s numa tacada de 12 m/s. NÃO CABE.
   * Já `R·ω` tem a mesma ordem de grandeza da velocidade, e sobra folga.
   *
   * E as equações ficam mais limpas: multiplicando `dω/dt = −(5μg)/(2R)·(k̂×û)`
   * por R, o raio DESAPARECE — `ds/dt = −(5μg/2)(k̂×û)`. Uma divisão a menos por
   * passo, e uma fonte de erro a menos.
   *
   * `x` e `y` são a componente horizontal (a que faz a bola correr, recuar e
   * curvar). O giro em torno do eixo vertical vive em `spinZ`.
   */
  spin: Vec

  /**
   * Efeito lateral, também como velocidade de superfície (`R·ωz`).
   *
   * Separado do vetor horizontal porque age por outro caminho: não empurra a
   * bola no pano, mas muda o ângulo de saída na tabela e produz *throw* na
   * bola-objeto. Decai por `MU_SPIN`, independente do resto.
   */
  spinZ: Fixed

  /**
   * A bola já pegou o pano e está rolando, em vez de patinar?
   *
   * Escrito a cada passo e NUNCA lido pela simulação — serve para teste e para
   * a interface. Fica de fora do `hashState` de propósito: é derivado de
   * `velocity` e `spin`, que já entram, e conferir derivado é conferir duas
   * vezes a mesma coisa.
   */
  rolling: boolean

  /*
   * ---------------------------------------------------------------------
   * `spinDecay` foi REMOVIDO daqui.
   *
   * Era um campo por bola, atribuído em `beginShot` a partir da aderência do
   * couro do taco, copiado no `cloneState`, com um comentário explicando que
   * "viaja com a bola porque é o efeito DELA que está decaindo" — e nenhuma
   * linha da simulação jamais o leu. Na v2 ele decaía o efeito a cada passo;
   * na v3 o decaimento passou a vir do atrito de duas fases e o campo ficou.
   *
   * A prova de que estava morto: removê-lo por inteiro não mexeu no digest da
   * física (`1d6caf50` antes e depois).
   *
   * A aderência do couro CONTINUA valendo — ela modula o efeito imprimido, em
   * `replay.ts`, via `spinDecayFor(taco)`. O que não existia era este caminho.
   * ---------------------------------------------------------------------
   */
  /** Encaçapada sai da mesa e para de participar da simulação. */
  pocketed: boolean
}

export type TableState = {
  balls: Ball[]
}

/**
 * ONDE cada bola estava no instante do contato: `emA`/`emB` para o contato
 * entre bolas, `em` para o da tabela.
 *
 * Não entram no `hashEvents` (ele enumera os campos um a um) e não mudam a
 * física: existem para quem DESENHA. A mira precisa do ponto do toque para o
 * ricochete virar uma quina e para a bola fantasma cair no lugar; a posição no
 * fim do passo já não serve, porque o passo continua depois de resolver o
 * contato — na v6 ela servia por acidente, já que o resto do passo era
 * descartado (#69).
 *
 * São DOIS pontos no contato entre bolas, e não um, porque quem desenha precisa
 * saber onde está uma bola específica (a branca, em geral) e não o meio entre
 * as duas. Guardar só o centro de `a` obrigava quem lê a confiar em que a
 * branca é sempre `a` — verdade hoje, por ela ser `balls[0]` e `contatosEm`
 * varrer `i<j`, e exatamente o tipo de invariante que cala um erro no dia em
 * que deixa de valer.
 *
 * O ponto da tabela é o do contato DEPOIS do ajuste que cola a bola na parede
 * (ver `resolveCushionCollision`): é a posição a exatamente um raio da parede,
 * que é o que a mira quer desenhar.
 */
export type CollisionEvent =
  | { type: 'ball-ball'; a: number; b: number; speed: Fixed; emA: Vec; emB: Vec }
  | { type: 'ball-cushion'; ball: number; cushion: T.Cushion; speed: Fixed; em: Vec }
  | { type: 'pocketed'; ball: number; pocket: number }

export function createBall(id: number, x: Fixed, y: Fixed): Ball {
  return {
    id,
    position: V.vec(x, y),
    velocity: V.vec(0, 0),
    spin: V.vec(0, 0),
    spinZ: 0,
    rolling: false,
    pocketed: false,
  }
}

export function cloneState(state: TableState): TableState {
  return {
    balls: state.balls.map((b) => ({
      id: b.id,
      position: V.clone(b.position),
      velocity: V.clone(b.velocity),
      spin: V.clone(b.spin),
      spinZ: b.spinZ,
      rolling: b.rolling,
      pocketed: b.pocketed,
    })),
  }
}

/** Alguma bola ainda está em movimento? */
export function isMoving(state: TableState): boolean {
  for (const ball of state.balls) {
    if (ball.pocketed) continue
    /*
     * Velocidade OU rotação. A rotação sozinha ainda move a bola.
     *
     * Olhar só a velocidade descreve a v2, onde o efeito era gasto no impacto.
     * Na v3 uma bola pode estar com velocidade zero e giro sobrando — é
     * exatamente o instante do meio de uma tacada de recuo, quando a branca
     * para antes de voltar. O atrito age nesse estado e a bola volta a andar.
     *
     * Enquanto a conferência olhava só a velocidade, `simulate` podia encerrar
     * ali e a branca ficava congelada em vez de recuar. Não consegui reproduzir
     * em 3000 tacadas — a velocidade precisa cair EXATAMENTE em zero num limite
     * de passo, e ela anda ~1875 unidades de Q16.16 por passo nessa fase, o que
     * dá algo como uma tacada em duas mil. É pouco, e num jogo de aposta "uma
     * em duas mil" é um jogador por semana vendo a tacada dele morrer sem
     * explicação.
     */
    if (!V.isZero(ball.velocity) || !V.isZero(ball.spin)) return true
  }
  return false
}

// --------------------------------------------------- tempo até o contato

/** Nenhum contato dentro do intervalo. */
const NO_HIT = -1

/**
 * Instante em que duas bolas se tocam, dentro de `limite`.
 *
 * Resolve |d + v·t| = 2R para t. Só interessa a raiz menor (primeiro contato)
 * e apenas quando elas estão se aproximando — bolas em contato que já se
 * separam não devem colidir de novo e travar a simulação.
 */
function timeToBallContact(a: Ball, b: Ball, limite: Fixed): Fixed {
  const d = V.sub(b.position, a.position)
  const v = V.sub(b.velocity, a.velocity)

  const vv = V.lengthSq(v)
  if (vv === 0) return NO_HIT

  const dv = V.dot(d, v)
  if (dv >= 0) return NO_HIT // afastando-se

  const dd = V.lengthSq(d)
  const c = dd - T.CONTACT_DISTANCE_SQ

  // Já sobrepostas e se aproximando: resolve agora, senão afundam uma na outra.
  if (c <= 0) return 0

  const discriminante = F.sqr(dv) - F.mul(vv, c)
  if (discriminante < 0) return NO_HIT

  const t = F.div(-dv - F.sqrt(discriminante), vv)
  return t >= 0 && t <= limite ? t : NO_HIT
}

/** Onde e quando a bola encosta numa parede. */
type ContatoParede = {
  time: Fixed
  wall: T.Wall
  /** Normal no ponto de contato: a da face, ou radial quando é a ponta. */
  normal: Vec
  /** Ponto da parede que serve de referência para colar a bola nela. */
  origem: Vec
}

const BALL_RADIUS_SQ: Fixed = F.sqr(T.BALL_RADIUS)
const FOLGA_DE_ALCANCE: Fixed = F.from(0.01)

/**
 * Instante em que a bola encosta num PONTO fixo (a ponta de uma parede).
 *
 * É a mesma conta de `timeToBallContact`, com a outra bola parada e de raio
 * zero: resolve |d + v·t| = R.
 */
function timeToPointContact(p: Vec, v: Vec, ponta: Vec, limite: Fixed): Fixed {
  const d = V.sub(p, ponta)
  const vv = V.lengthSq(v)
  if (vv === 0) return NO_HIT

  const dv = V.dot(d, v)
  if (dv >= 0) return NO_HIT // afastando-se

  const c = V.lengthSq(d) - BALL_RADIUS_SQ
  if (c <= 0) return 0

  const discriminante = F.sqr(dv) - F.mul(vv, c)
  if (discriminante < 0) return NO_HIT

  const t = F.div(-dv - F.sqrt(discriminante), vv)
  return t >= 0 && t <= limite ? t : NO_HIT
}

/**
 * Instante em que a bola toca uma parede, e qual.
 *
 * Até a v4 eram quatro retas infinitas, uma por lado, e a conta era uma
 * divisão. Com bocas a mesa é uma lista de TRECHOS (`T.WALLS`): as tabelas
 * entre os narizes, as faces das bocas, o fundo de cada caçapa. Cada trecho
 * tem duas partes que a bola pode tocar:
 *
 *   - a FACE, dentro do comprimento do trecho — contato plano, normal da face;
 *   - as PONTAS, os narizes — contato redondo, normal radial. É isso que faz a
 *     bola "rasparo nariz" e entrar (ou não) na boca, em vez de atravessar a
 *     quina ou bater numa parede que não existe.
 *
 * Empate de tempo é resolvido pela ordem de `T.WALLS`, que é fixa: faz parte
 * da física, e do replay.
 */
function timeToCushionContact(ball: Ball, limite: Fixed): ContatoParede | null {
  const { position: p, velocity: v } = ball
  if (V.isZero(v)) return null

  let melhor: ContatoParede | null = null
  const considerar = (time: Fixed, wall: T.Wall, normal: Vec, origem: Vec) => {
    if (time < 0 || time > limite) return
    if (!melhor || time < melhor.time) melhor = { time, wall, normal, origem }
  }

  /*
   * Até onde a bola consegue chegar neste intervalo: o raio dela mais o que
   * anda no tempo que resta (|vx| + |vy| é um teto barato de |v|). Qualquer
   * parede cuja caixa envolvente fique fora desse alcance não precisa de
   * conta nenhuma — e no meio da mesa isso é TODAS. Sem este corte a bateria
   * de referência ficou 4× mais lenta com as bocas.
   */
  // A folga de 1 cm cobre o caso em que `limite` é zero — contato AGORA —
  // e a bola está a um fio de arredondamento além da distância de toque.
  const alcance = T.BALL_RADIUS + F.mul(F.abs(v.x) + F.abs(v.y), limite) + FOLGA_DE_ALCANCE
  const minX = p.x - alcance
  const maxX = p.x + alcance
  const minY = p.y - alcance
  const maxY = p.y + alcance

  for (const wall of T.WALLS) {
    if (wall.maxX < minX || wall.minX > maxX || wall.maxY < minY || wall.minY > maxY) continue

    const rel = V.sub(p, wall.a)

    // A face. Só interessa se a bola está indo CONTRA ela.
    const vn = V.dot(v, wall.normal)
    if (vn < 0) {
      const dist = V.dot(rel, wall.normal) - T.BALL_RADIUS
      // Já encostada (ou um fio dentro, por arredondamento) e se aproximando:
      // resolve agora, senão afunda na parede.
      const t = dist <= 0 ? 0 : F.div(dist, -vn)
      if (t <= limite) {
        // O ponto de contato tem de cair DENTRO do trecho. Fora dele, quem
        // responde é a ponta — ou trecho nenhum, que é o que abre a boca.
        const along = V.dot(rel, wall.dir) + F.mul(V.dot(v, wall.dir), t)
        if (along >= 0 && along <= wall.length) considerar(t, wall, wall.normal, wall.a)
      }
    }

    // As pontas.
    for (const ponta of [wall.a, wall.b]) {
      const t = timeToPointContact(p, v, ponta, limite)
      if (t === NO_HIT) continue
      const normal = V.normalize(
        V.vec(p.x + F.mul(v.x, t) - ponta.x, p.y + F.mul(v.y, t) - ponta.y),
      )
      if (V.isZero(normal)) continue
      considerar(t, wall, normal, ponta)
    }
  }

  return melhor
}

// -------------------------------------------------------------- resolução

/**
 * Impulso de uma colisão entre bolas de massa igual — CALCULA, não aplica.
 *
 * A separação entre calcular e aplicar não é estilo: é o que permite resolver
 * contatos simultâneos a partir do mesmo estado. As velocidades vêm por
 * parâmetro, de um retrato congelado, justamente para que um impulso já
 * aplicado a uma das bolas não contamine o cálculo do outro.
 *
 * Só a componente ao longo da linha de centros é trocada; a tangencial passa
 * intacta. É o que faz a bola objeto sair na direção do contato e a branca
 * desviar — o comportamento que todo jogador de sinuca conhece.
 *
 * A v2 transferia o efeito para velocidade AQUI, no impacto, e o gastava. A v3
 * não faz nada disso, e a ausência é a mudança: a rotação sobrevive à colisão e
 * continua agindo pelo pano, então a branca corre atrás da bola ou recua ao
 * longo dos passos seguintes em vez de num pulo instantâneo. Manter as duas
 * coisas contaria o efeito duas vezes.
 */
function impulsoEntreBolas(
  a: Ball,
  b: Ball,
  va: { x: Fixed; y: Fixed },
  vb: { x: Fixed; y: Fixed },
): { normal: Vec; tangente: Vec; j: Fixed; jt: Fixed; velocidade: Fixed } | null {
  const normal = V.normalize(V.sub(b.position, a.position))
  // Duas bolas exatamente no mesmo ponto não têm linha de centros. Sem esta
  // saída o tempo de contato ficaria zero para sempre e o passo queimaria as
  // 64 iterações sem avançar — a simulação inteira travaria em silêncio.
  if (V.isZero(normal)) return null

  const aproximacao = F.mul(vb.x - va.x, normal.x) + F.mul(vb.y - va.y, normal.y)
  // Já se afastando: resolver de novo grudaria as duas.
  if (aproximacao >= 0) return null

  // Massas iguais: a troca é `(1+e)/2` da componente normal, e o impulso é
  // aplicado inteiro nas duas — ver BALL_IMPULSE_FACTOR, que existe justamente
  // porque usar `e` aqui dá uma restituição efetiva de `2e−1`.
  const j = F.mul(aproximacao, T.BALL_IMPULSE_FACTOR)

  /*
   * O THROW: atrito entre as duas bolas, na direção TANGENTE.
   *
   * `MU_BALL` existia como constante desde sempre, com um comentário dizendo
   * "é ele que produz o throw" — e nunca foi usado por linha nenhuma. O efeito
   * lateral, por consequência, não desviava a bola alvo: o jogador aplicava
   * inglês e o alvo saía exatamente na linha dos centros, como se nada tivesse
   * sido feito. Metade das tacadas difíceis do bilhar depende disto.
   *
   * A superfície da branca no ponto de contato se move em relação à do alvo, e
   * o atrito de Coulomb resiste a esse deslizamento com teto `μ·|impulso
   * normal|`. É por isso que o throw satura: passado o teto, as superfícies
   * agarram e mais efeito não desvia mais.
   *
   * A velocidade relativa das superfícies tem duas fontes, e as duas contam: o
   * corte (componente tangencial das velocidades dos centros) e o giro em torno
   * do eixo vertical, que é onde o inglês mora — `spinZ` guardado como `R·ωz`,
   * ou seja já em m/s de superfície.
   */
  const tangente = V.vec(-normal.y, normal.x)
  /*
   * O SINAL do termo de giro, que é fácil de errar e eu errei.
   *
   * A superfície da branca no ponto de contato — que fica na direção `+n̂` a
   * partir do centro dela — se move a `ω × (R n̂) = spinZ · t̂`. A do alvo, cujo
   * ponto de contato fica em `−n̂`, se move a `−spinZ · t̂`. O deslizamento
   * relativo de b em relação a a é, portanto, `(v_b − v_a)·t̂ − (spinZ_a +
   * spinZ_b)`.
   *
   * Com o sinal trocado o throw acontecia com a magnitude certa e para o LADO
   * ERRADO: quem aplicasse efeito à direita veria o alvo desviar para a
   * esquerda. Foi o simulador de referência em Rust que apontou — escrito a
   * partir da literatura, sem olhar este arquivo, justamente para não herdar o
   * erro. Aqui as duas implementações discordaram no sinal, e a derivação
   * acima decidiu a favor dele.
   */
  const deslizeTangente =
    F.mul(vb.x - va.x, tangente.x) + F.mul(vb.y - va.y, tangente.y) - a.spinZ - b.spinZ

  let jt = 0
  if (deslizeTangente !== 0) {
    const teto = F.mul(T.MU_BALL, F.abs(j))
    // O fator 2/7 vem do momento de inércia da esfera: parte do impulso
    // tangencial vira rotação em vez de translação.
    const pedido = F.mul(deslizeTangente, F.from(2 / 7))
    const magnitude = Math.min(F.abs(pedido), teto)
    jt = pedido > 0 ? magnitude : -magnitude
  }

  return { normal, tangente, j, jt, velocidade: F.abs(aproximacao) }
}

function resolveCushionCollision(ball: Ball, contato: ContatoParede): Fixed {
  const n = contato.normal
  const vn = V.dot(ball.velocity, n) // negativo: a bola vem contra a parede
  const velocidade = F.abs(vn)

  // Reflexão com restituição: só a componente normal inverte, e encolhe.
  //   v' = v − (1 + e)·(v·n)·n
  const fator = F.mul(F.ONE + T.CUSHION_RESTITUTION, vn)
  ball.velocity.x -= F.mul(fator, n.x)
  ball.velocity.y -= F.mul(fator, n.y)

  /*
   * Efeito lateral muda o ÂNGULO de saída: a bola abre ou fecha em relação ao
   * que a reflexão pura daria. É o recurso que permite escapar de snookers.
   *
   * A superfície da bola no ponto de contato se move a `spinZ·(ẑ × n̂)`, com
   * `n̂` apontando da parede para a bola, e o atrito da borracha empurra a
   * bola nessa direção: Δv = k·spinZ·perp(n̂), com perp(x, y) = (−y, x).
   *
   * Conferindo com a tabela de sinais da v4, que tinha as quatro escritas à
   * mão: esquerda n̂=(1,0) → +k em y; direita n̂=(−1,0) → −k em y; baixo
   * n̂=(0,1) → −k em x; cima n̂=(0,−1) → +k em x. Bate nas quatro — e agora
   * vale também para as faces inclinadas das bocas e para os narizes, onde
   * uma tabela de quatro linhas não teria resposta.
   */
  const desvio = F.mul(ball.spinZ, T.SPIN_CUSHION_EFFECT)
  ball.velocity.x += F.mul(desvio, -n.y)
  ball.velocity.y += F.mul(desvio, n.x)

  // Cola na parede: sem isto, erro de arredondamento deixa a bola um fio
  // dentro e ela dispara outra colisão imediatamente.
  const dist = V.dot(V.sub(ball.position, contato.origem), n)
  if (dist < T.BALL_RADIUS) {
    const empurrao = T.BALL_RADIUS - dist
    ball.position.x += F.mul(empurrao, n.x)
    ball.position.y += F.mul(empurrao, n.y)
  }

  ball.spinZ = F.mul(ball.spinZ, T.SPIN_RETENTION)

  return velocidade
}

// ------------------------------------------------------------- integração

function advance(state: TableState, dt: Fixed): void {
  for (const ball of state.balls) {
    if (ball.pocketed || V.isZero(ball.velocity)) continue
    V.addScaledInto(ball.position, ball.velocity, dt)
  }
}

/**
 * Atrito com o pano, em duas fases: deslizando e rolando.
 *
 * É o coração da v3, e o que a v2 não tinha. O modelo é o padrão da literatura
 * (Leckie & Greenspan; Alciatore), reescrito em velocidade de superfície.
 *
 * A grandeza que manda é a velocidade RELATIVA no ponto de contato:
 *
 *     u = v + (k̂ × s)
 *
 * Enquanto `u ≠ 0` a bola PATINA: o pano a freia em `−μs·g·û` e, pela mesma
 * fricção, empurra a rotação na direção oposta. As duas convergem, e `|u|` cai
 * a `(7/2)·μs·g` — é dessa convergência que saem a curva no percurso e o recuo
 * que dura, coisas impossíveis num modelo que só age no impacto.
 *
 * Quando `u` zera a bola ROLA, e aí o atrito de rolamento (cinquenta vezes
 * menor) assume, com a rotação amarrada à velocidade.
 */
function applyFriction(state: TableState, dt: Fixed): void {
  // Desacelerações por passo, todas em m/s.
  const aDeslize = F.mul(F.mul(T.MU_SLIDE, T.GRAVITY), dt)
  const aRolagem = F.mul(F.mul(T.MU_ROLL, T.GRAVITY), dt)
  const aGiro = F.mul(F.mul(T.MU_SPIN, T.GRAVITY), dt)

  for (const ball of state.balls) {
    if (ball.pocketed) continue

    // O efeito lateral decai sozinho, mesmo com a bola parada: uma bola girando
    // no lugar perde o giro para o pano.
    if (ball.spinZ !== 0) {
      const perda = F.mul(aGiro, F.from(2.5))
      if (F.abs(ball.spinZ) <= perda) ball.spinZ = 0
      else ball.spinZ += ball.spinZ > 0 ? -perda : perda
    }

    if (V.isZero(ball.velocity) && V.isZero(ball.spin)) continue

    // u = v + (k̂ × s). O produto vetorial com o vertical roda 90°: (x,y) → (−y,x).
    const u = V.vec(ball.velocity.x - ball.spin.y, ball.velocity.y + ball.spin.x)
    const uMod = V.length(u)

    if (uMod > T.ROLL_THRESHOLD) {
      // ------------------------------------------------------- deslizando
      ball.rolling = false

      const ux = F.div(u.x, uMod)
      const uy = F.div(u.y, uMod)

      /*
       * O ÚLTIMO passo do deslizamento é encurtado para `u` aterrissar no zero.
       *
       * Sem isto o atrito subtrai sempre a mesma mordida, não importa quão
       * perto da transição a bola esteja: quando falta menos que uma mordida,
       * ela PASSA DO PONTO, `u` inverte de sinal e volta a crescer, e a bola
       * fica alternando entre deslizar e quase-rolar.
       *
       * A correção é de princípio, não de sintoma: eu a escrevi perseguindo um
       * serrilhado na resposta ao efeito de corrida e ela NÃO era a causa —
       * aquilo era a branca alcançando a bola alvo e beijando de novo, que é
       * física legítima. Fica registrado para ninguém procurar aqui de novo.
       *
       * Ainda assim vale: passar do ponto e inverter o sentido de `u` é errado
       * em qualquer circunstância, e sem esta fração a bola oscilava entre
       * deslizar e quase-rolar no fim de toda tacada.
       *
       * `|u|` cai a `(7/2)·μs·g` por segundo — o mesmo resultado da literatura
       * que o sinal do torque abaixo produz. Quando o que falta é menos que um
       * passo inteiro, aplica-se só a fração que falta.
       */
      const quedaDeU = F.mul(aDeslize, F.from(3.5))
      const fracao = uMod < quedaDeU ? F.div(uMod, quedaDeU) : F.ONE
      const freio = F.mul(aDeslize, fracao)

      /*
       * A velocidade é freada na direção de `u` — SEM travar no zero.
       *
       * Travar seria intuitivo e errado: numa tacada de recuo a branca chega ao
       * alvo patinando para trás, `u` aponta para a frente, e o atrito a
       * empurra até a velocidade inverter. É assim que ela volta. Impedir a
       * inversão mataria o efeito de recuo, que é metade da razão de existir
       * desta versão.
       *
       * Quem para a bola é a fase de rolagem, quando `u` zera.
       */
      ball.velocity.x -= F.mul(ux, freio)
      ball.velocity.y -= F.mul(uy, freio)

      /*
       * A rotação recebe o mesmo atrito como TORQUE, com fator 5/2 vindo do
       * momento de inércia da esfera (I = ⅖mR²). Em velocidade de superfície o
       * R sai da conta: `ds/dt = (5μg/2)(k̂ × û)`.
       *
       * O SINAL É POSITIVO, e é fácil errar. A força age no ponto de CONTATO,
       * abaixo do centro, então o torque é `(−R k̂) × (−μmg û)` — os dois sinais
       * negativos se cancelam.
       *
       * A conferência que fecha: com este sinal, `du/dt = −μgû + k̂×(ds/dt)` dá
       * `−(7/2)μgû`, que é o resultado da literatura — `u` encolhe e a bola
       * conquista a rolagem. Com o sinal trocado dá `+(3/2)μgû`: a rotação
       * realimenta o deslizamento e cresce sem limite. Foi o que aconteceu na
       * primeira tentativa, e o sintoma foi spin passando de 90 m/s com a bola
       * parada.
       */
      const aGiroDeslize = F.mul(freio, F.from(2.5))
      ball.spin.x -= F.mul(uy, aGiroDeslize)
      ball.spin.y += F.mul(ux, aGiroDeslize)
    } else {
      // ---------------------------------------------------------- rolando
      //
      // Gruda no estado de rolagem: em passo fixo `|u|` nunca chega a zero
      // exato, e sem isto a bola oscilaria entre as duas fases. A oscilação é
      // diferença de arredondamento — ou seja, divergência entre máquinas.
      ball.rolling = true

      const velocidade = V.length(ball.velocity)
      if (velocidade <= aRolagem || velocidade < T.REST_SPEED) {
        V.set(ball.velocity, 0, 0)
        V.set(ball.spin, 0, 0)
        continue
      }

      const fator = F.div(velocidade - aRolagem, velocidade)
      V.scaleInto(ball.velocity, ball.velocity, fator)

      /*
       * Rolar É a rotação amarrada à velocidade.
       *
       * De `u = v + (k̂ × s) = 0` sai `s = (−v.y, v.x)`. A ordem dos sinais
       * importa e é fácil de inverter: trocá-los faz `u` valer o DOBRO da
       * velocidade em vez de zero, e a bola nunca para de patinar.
       */
      V.set(ball.spin, -ball.velocity.y, ball.velocity.x)
    }
  }
}

/**
 * Quantas vezes cada correção posicional varre a mesa por passo.
 *
 * Empurrar um par separado pode encostar esse par em outro — na quebra as bolas
 * estão em cadeia —, e o limite do pano, ao trazer uma bola para dentro, pode
 * encostá-la em outra. OITO passes: medido nas 40 quebras de referência, a
 * sobreposição em repouso chega a zero com 8 e não melhora com 16 nem com 32,
 * enquanto com 4 ficava em 1,27 mm. O custo é desprezível perto de um passo da
 * física (as quebras levam 1005 passos em média, contra 1004 com 4 passes).
 */
const PASSES_DA_SEPARACAO = 8

/**
 * Desencosta o que ficou sobreposto, e traz de volta quem furou a tabela.
 *
 * Nem a v6 nem as versões antes dela tinham correção POSICIONAL: a colisão
 * trocava velocidades e a posição ficava como estava. Isso deixava duas marcas,
 * e a v6 escondia as duas por acidente, porque descartava o resto do passo e
 * congelava as bolas no instante do contato (issue #69):
 *
 *   - bolas em repouso ATRAVESSADAS uma na outra — medido na v6: 0,36 mm; na v7
 *     antes desta função, 1,95 mm;
 *   - bolas paradas FORA do pano — medido na v6: 18 em 40 quebras; na v7 antes
 *     desta função, 5. As paredes valem de um lado só (`timeToCushionContact`
 *     exige velocidade contra a face), então quem passou da linha com a
 *     velocidade para fora nunca mais encontrava contato: ficava lá.
 *
 * A correção é geométrica e simétrica: cada bola de um par sobreposto recebe
 * METADE da penetração ao longo da linha de centros, e quem está além do limite
 * do pano volta para o limite. Nenhuma velocidade é tocada — isto não cria nem
 * destrói energia, só desfaz a interpenetração que o impulso não desfaz.
 *
 * SÓ MEXE EM BOLA PARADA, e isso não é detalhe: a primeira versão desta função
 * empurrava qualquer par sobreposto e virou uma prisão. Uma bola com velocidade
 * contra outra era devolvida todo passo — tinha 1,9 m/s e andava 0,00 mm —, e a
 * quebra da semente 3 passou de 706 para 4.877 passos, porque só o atrito
 * conseguia soltá-la. A separação não pode disputar com a dinâmica: quem está em
 * movimento é assunto do impulso, e a interpenetração que sobra é desfeita
 * quando as bolas assentam.
 *
 * A ordem é bolas e depois paredes, de propósito: a parede é restrição dura (não
 * existe "meio dentro do pano"), então ela fala por último e ninguém a desfaz.
 */
/**
 * UM passe de separação. Devolve se mexeu em alguma bola.
 *
 * Um passe, e não um laço, porque quem chama é que sabe com que outra restrição
 * alternar — e porque aninhar laços aqui custou caro: `desencostarBolas` com
 * passes internos, chamada dentro de um laço de oito, dava 64 varreduras O(n²)
 * por passo da física, e os testes do replay começaram a estourar o prazo de 5 s.
 */
function umPasseDeDesencostar(state: TableState, soParadas: boolean): boolean {
  const diametro = T.BALL_RADIUS + T.BALL_RADIUS
  {
    let mexeu = false
    const ativas = state.balls.filter(
      (b) => !b.pocketed && (!soParadas || V.isZero(b.velocity)),
    )

    for (let i = 0; i < ativas.length; i++) {
      const a = ativas[i]!
      for (let j = i + 1; j < ativas.length; j++) {
        const b = ativas[j]!
        const d = V.sub(b.position, a.position)
        const dist = V.length(d)
        if (dist >= diametro) continue

        /*
         * Par que ainda se APROXIMA é assunto do impulso: separá-lo é empurrar
         * uma bola contra o movimento dela, e foi assim que a primeira versão
         * disto virou prisão. Quando a aproximação já é zero ou negativa, o
         * impulso não tem mais nada a fazer e o que sobra é geometria.
         */
        const aproximacao = F.mul(b.velocity.x - a.velocity.x, d.x) + F.mul(b.velocity.y - a.velocity.y, d.y)
        if (aproximacao < 0) continue

        /*
         * Centros exatamente em cima um do outro não têm direção de separação.
         * Não acontece na prática — duas bolas precisariam ter a mesma posição
         * em ponto fixo —, e sair sem mexer é melhor que inventar uma direção
         * que depende da ordem do array e quebraria o determinismo.
         */
        if (dist === 0) continue

        const metade = F.div(diametro - dist, F.from(2))
        const n = V.normalize(d)
        a.position.x -= F.mul(metade, n.x)
        a.position.y -= F.mul(metade, n.y)
        b.position.x += F.mul(metade, n.x)
        b.position.y += F.mul(metade, n.y)
        mexeu = true
      }
    }

    return mexeu
  }
}

/** Desencosta até ninguém mais se mover, no teto de `PASSES_DA_SEPARACAO`. */
function desencostarBolas(state: TableState, soParadas = false): void {
  for (let passe = 0; passe < PASSES_DA_SEPARACAO; passe++) {
    if (!umPasseDeDesencostar(state, soParadas)) break
  }
}

/**
 * Traz para o pano quem PAROU fora dele.
 *
 * Roda no fim do passo, depois do atrito, e é separada de `desencostarBolas` por
 * um motivo medido: as duas correções pertencem a momentos diferentes. Desencostar
 * precisa das velocidades logo depois da colisão (com as do pós-atrito, o
 * critério de aproximação deixa pares de fora e a sobreposição no impacto subiu
 * de 0,72 para 2,41 mm). Já esta precisa do DEPOIS: a bola empurrada para fora
 * que o atrito para no mesmo passo não tem passo seguinte onde ser resgatada —
 * medido, 2 bolas perdidas a 65 e 89 mm da caçapa mais próxima.
 *
 * Só para quem está fora e PARADO: uma bola na BOCA de uma caçapa tem o centro
 * legitimamente além da linha da tabela, e é `pocketAt` que decide o destino
 * dela — por isso o limite não é aplicado a quem `pocketAt` reclamou.
 */
function trazerParaOPano(state: TableState): boolean {
  let mexeu = false

  for (const ball of state.balls) {
    if (ball.pocketed || !V.isZero(ball.velocity)) continue
    if (T.pocketAt(ball.position) >= 0) continue
    const x = F.clamp(ball.position.x, T.CUSHION_BOUNDS.minX, T.CUSHION_BOUNDS.maxX)
    const y = F.clamp(ball.position.y, T.CUSHION_BOUNDS.minY, T.CUSHION_BOUNDS.maxY)
    if (x !== ball.position.x || y !== ball.position.y) mexeu = true
    ball.position.x = x
    ball.position.y = y
  }

  return mexeu
}

function capturePockets(state: TableState, events: CollisionEvent[]): void {
  for (const ball of state.balls) {
    if (ball.pocketed) continue

    const pocket = T.pocketAt(ball.position)
    if (pocket < 0) continue

    ball.pocketed = true
    V.set(ball.velocity, 0, 0)
    V.set(ball.spin, 0, 0)
    events.push({ type: 'pocketed', ball: ball.id, pocket })
  }
}

/**
 * Avança um passo de tempo, resolvendo colisões na ordem em que acontecem.
 *
 * O laço interno existe porque um único passo pode conter várias colisões — na
 * quebra, dezenas. Resolver todas na ordem correta é o que separa uma
 * simulação plausível de uma que empurra bolas para dentro umas das outras.
 */
export function step(state: TableState, dt: Fixed = T.DT): CollisionEvent[] {
  const events: CollisionEvent[] = []
  let restante = dt

  // Teto de resoluções por passo. Uma quebra bem no meio gera muitas, mas
  // centenas indicariam bug — e o laço precisa terminar de qualquer forma.
  for (let iteracao = 0; iteracao < 64 && restante > 0; iteracao++) {
    const ativas = state.balls.filter((b) => !b.pocketed)

    const menorTempo = proximoContato(ativas, restante)
    /*
     * TODOS os contatos daquele instante, não só o primeiro.
     *
     * Enquanto se resolvia um por vez e o laço reavaliava, a segunda colisão
     * de um empate era resolvida com a velocidade JÁ alterada pela primeira —
     * e como a varredura visita as bolas na ordem do array, era o ÍNDICE que
     * decidia. Medido: branca entrando exatamente no meio de duas bolas
     * simétricas saía com vy = −0,275 e uma das bolas levava o dobro da
     * energia da outra; trocar as duas de lugar no array espelhava tudo.
     *
     * Isso é a quebra inteira, onde meia dúzia de contatos acontecem no mesmo
     * instante. Continuava determinístico — o replay conferia —, mas o que ele
     * reproduzia fielmente era uma ordem de lista, não uma colisão.
     */
    /*
     * Os contatos saem AQUI, antes de `advance`: é neste estado que eles
     * acontecem em `menorTempo`. Procurá-los depois de avançar não achava nada
     * — pós-`advance` o contato está a tempo ZERO, e a igualdade exata de
     * `contatosEm` falhava —, o laço saía pelo `break` abaixo e o que sobrava
     * do passo era descartado para TODAS as bolas (issue #69). Quem resolve
     * continua sendo `resolverSimultaneos`, com a mesa já avançada.
     */
    const contatos = contatosEm(ativas, menorTempo)
    advance(state, menorTempo)
    restante -= menorTempo

    /*
     * A CAÇAPA vem antes de resolver o contato, como na v6.
     *
     * Quem entrou está fora: resolver primeiro deixaria a bola já dentro da
     * boca levar a reflexão da parede do fundo e o empurrão do "cola na
     * parede", e um empurrão de poucos milímetros ali decide se ela é
     * capturada ou volta ao pano — muda o DESFECHO da tacada, não a
     * trajetória. Na v6 a captura ganhava porque o contato só era resolvido no
     * passo seguinte, depois de `capturePockets`; coletar os contatos antes do
     * `advance` inverteu essa ordem sem querer.
     */
    capturePockets(state, events)

    /*
     * E o contato de quem caiu não vale: a bola saiu da mesa neste instante, e
     * aplicar o impulso dela empurraria as outras com uma bola que não está
     * mais lá.
     */
    const valem = contatos.filter((c) =>
      c.tipo === 'bola' ? !c.a.pocketed && !c.b.pocketed : !c.bola.pocketed,
    )

    if (valem.length === 0) break

    events.push(...resolverSimultaneos(valem))
    capturePockets(state, events)
  }

  /*
   * O teto de iterações não pode engolir o resto do passo: um grupo de bolas
   * encostadas gasta as 64 voltas em contatos de tempo zero e sairia daqui com
   * `restante` no lugar, sem ninguém ter andado (issue #69).
   *
   * ATÉ O PRÓXIMO CONTATO, e não o `restante` inteiro. Integrar reto era mais
   * simples e atravessava: medido, 203 passos numa bateria de 39 mil chegavam
   * aqui com até um `dt` cheio por integrar, movendo bolas 32,8 mm — mais de
   * meia bola — sem checar colisão nenhuma. E as paredes valem de um lado só
   * (`timeToCushionContact` exige velocidade CONTRA a face), então uma bola
   * empurrada além da linha da tabela com a velocidade para fora nunca mais
   * encontraria contato: ficaria parada fora da mesa, para sempre.
   *
   * Limitado ao contato, nada é atravessado. O tempo entre esse contato e o fim
   * do passo continua perdido, mas só nos passos que esgotam o teto — e perder
   * tempo é um erro de grau, enquanto perder a bola é um erro de espécie.
   */
  if (restante > 0) {
    const ativas = state.balls.filter((b) => !b.pocketed)
    advance(state, proximoContatoQueImporta(ativas, restante))
  }

  capturePockets(state, events)
  // Com as velocidades da colisão ainda na mão: ver `desencostarBolas`.
  desencostarBolas(state)
  /*
   * O atrito age sobre o passo INTEIRO, e é isso que se quer.
   *
   * Parece que deveria valer só o tempo integrado — a issue #69 sugere
   * exatamente isso. Está errado: quando o teto de iterações estoura, sobra
   * tempo não integrado, `dt - restante` encolhe o atrito a quase nada, nada
   * dissipa e nada escapa. Medido: 12 de 40 quebras NUNCA param, com duas bolas
   * em 4,6 m/s indefinidamente. O atrito cheio é a válvula de escape do motor.
   */
  applyFriction(state, dt)

  /*
   * E o ASSENTAMENTO, com a parede falando por último.
   *
   * O limite do pano empurra para dentro, e esse empurrão pode encostar a bola
   * em outra — rodar só o limite deixou pares com 22 mm de sobreposição, porque
   * ninguém os desfazia depois. Então os dois alternam: desencosta quem parou,
   * traz para o pano, repete. A parede é restrição dura e fecha a conta, e a
   * sobreposição que sobra é a de quem ainda está em movimento, que o próximo
   * passo resolve.
   */
  for (let passe = 0; passe < PASSES_DA_SEPARACAO; passe++) {
    /*
     * Os DOIS têm de responder antes de parar. Saindo quando só o desencostar
     * não mexeu, o empurrão que o limite do pano acabou de dar ficava sem
     * ninguém para desfazer, e pares em repouso terminavam com 22 mm de
     * sobreposição. A parede fecha cada volta, por ser restrição dura.
     */
    const desencostou = umPasseDeDesencostar(state, true)
    const trouxe = trazerParaOPano(state)
    if (!desencostou && !trouxe) break
  }

  return events
}

/**
 * O próximo contato de tempo ESTRITAMENTE positivo, ou `limite`.
 *
 * Só para o avanço final, o dos passos que esgotaram o teto de iterações. O que
 * sobra ali são contatos de tempo zero que se repetem sem efeito: bolas
 * encostadas que já se separam, para as quais `impulsoEntreBolas` devolve
 * `null`. Enquanto `proximoContato` responder zero a eles, o passo não anda —
 * medido, 8 de 40 quebras ainda travavam, até 43 passos seguidos, e subir o teto
 * para 1024 não mudava nada, porque o problema não é falta de iteração.
 *
 * Passar por cima de um contato inócuo é diferente de atravessar um contato: os
 * de tempo positivo continuam limitando o avanço, então nada é perfurado.
 */
function proximoContatoQueImporta(ativas: Ball[], limite: Fixed): Fixed {
  let menor = limite

  for (let i = 0; i < ativas.length; i++) {
    const a = ativas[i]!

    const tabela = timeToCushionContact(a, menor)
    if (tabela && tabela.time > 0 && tabela.time < menor) menor = tabela.time

    for (let j = i + 1; j < ativas.length; j++) {
      const t = timeToBallContact(a, ativas[j]!, menor)
      if (t !== NO_HIT && t > 0 && t < menor) menor = t
    }
  }

  return menor
}

/** Instante do próximo contato, ou `limite` se nenhum acontece antes dele. */
function proximoContato(ativas: Ball[], limite: Fixed): Fixed {
  let menor = limite

  for (let i = 0; i < ativas.length; i++) {
    const a = ativas[i]!

    const tabela = timeToCushionContact(a, menor)
    if (tabela && tabela.time < menor) menor = tabela.time

    for (let j = i + 1; j < ativas.length; j++) {
      const t = timeToBallContact(a, ativas[j]!, menor)
      if (t !== NO_HIT && t < menor) menor = t
    }
  }

  return menor
}

/** Contato marcado para resolução, já sabendo quem encosta em quem. */
type Contato =
  | { tipo: 'bola'; a: Ball; b: Ball }
  | { tipo: 'tabela'; bola: Ball; contato: ContatoParede }

/**
 * Todos os contatos que acontecem EXATAMENTE em `quando`.
 *
 * A igualdade é exata porque tudo aqui é inteiro de ponto fixo: dois contatos
 * ou acontecem no mesmo instante ou não, sem tolerância a arbitrar. A ordem de
 * varredura é a do array e é preservada — não para decidir física, que agora
 * independe dela, mas para o arredondamento da soma dos impulsos ser
 * reprodutível.
 */
function contatosEm(ativas: Ball[], quando: Fixed): Contato[] {
  const contatos: Contato[] = []

  for (let i = 0; i < ativas.length; i++) {
    const a = ativas[i]!

    const tabela = timeToCushionContact(a, quando)
    if (tabela && tabela.time === quando) {
      contatos.push({ tipo: 'tabela', bola: a, contato: tabela })
    }

    for (let j = i + 1; j < ativas.length; j++) {
      const b = ativas[j]!
      if (timeToBallContact(a, b, quando) === quando) contatos.push({ tipo: 'bola', a, b })
    }
  }

  return contatos
}

/**
 * Resolve um conjunto de contatos simultâneos a partir do MESMO estado.
 *
 * As velocidades são lidas antes de qualquer alteração e todos os impulsos
 * saem desse retrato; só depois eles são somados e aplicados. É isso que torna
 * o resultado independente da ordem em que os contatos foram encontrados.
 */
function resolverSimultaneos(contatos: Contato[]): CollisionEvent[] {
  const eventos: CollisionEvent[] = []

  // O retrato congelado. Uma cópia por bola envolvida, e nada mais.
  const antes = new Map<Ball, { x: Fixed; y: Fixed }>()
  const congelar = (b: Ball) => {
    if (!antes.has(b)) antes.set(b, { x: b.velocity.x, y: b.velocity.y })
  }
  for (const c of contatos) {
    if (c.tipo === 'bola') {
      congelar(c.a)
      congelar(c.b)
    } else {
      congelar(c.bola)
    }
  }

  /*
   * Quantos contatos cada bola tem NESTE instante.
   *
   * Numa quebra as bolas do triângulo se tocam todas, e um impulso calculado
   * "como se este fosse o único contato" aplicado três vezes à mesma bola cria
   * energia do nada. Medido antes desta escala: um passo em que a energia total
   * da mesa subiu 548%, e a branca saía da quebra ANDANDO PARA TRÁS meia mesa.
   *
   * A escala é `1/max(k_a, k_b)`, a mesma dos dois lados do contato — é isso
   * que mantém o impulso igual e oposto e, com ele, a conservação do momento.
   * Dividir por `k` de cada bola separadamente conservaria energia e quebraria
   * momento, que é o pior dos dois.
   */
  const grau = new Map<Ball, number>()
  for (const c of contatos) {
    if (c.tipo !== 'bola') continue
    grau.set(c.a, (grau.get(c.a) ?? 0) + 1)
    grau.set(c.b, (grau.get(c.b) ?? 0) + 1)
  }

  const somar = new Map<Ball, { x: Fixed; y: Fixed }>()
  const acumular = (b: Ball, x: Fixed, y: Fixed) => {
    const atual = somar.get(b) ?? { x: 0, y: 0 }
    atual.x += x
    atual.y += y
    somar.set(b, atual)
  }

  /*
   * A tabela vem primeiro, e por um motivo: ela não é um impulso, é uma
   * restrição. Uma bola encostada no pano não pode sair atravessando-o, então
   * a reflexão precisa valer sobre a velocidade final, não competir com ela.
   */
  for (const c of contatos) {
    if (c.tipo !== 'tabela') continue
    const velocidade = resolveCushionCollision(c.bola, c.contato)
    eventos.push({
      type: 'ball-cushion',
      ball: c.bola.id,
      cushion: c.contato.wall.cushion,
      speed: velocidade,
      em: V.clone(c.bola.position),
    })
    // O retrato daquela bola acompanha a reflexão: o impulso da colisão com
    // outra bola tem de partir da velocidade que a tabela deixou.
    antes.set(c.bola, { x: c.bola.velocity.x, y: c.bola.velocity.y })
  }

  for (const c of contatos) {
    if (c.tipo !== 'bola') continue
    const impulso = impulsoEntreBolas(c.a, c.b, antes.get(c.a)!, antes.get(c.b)!)
    if (!impulso) continue

    // Normal mais tangente. O impulso tangencial é igual e oposto, como o
    // normal — o throw tira do alvo o que dá à branca, e vice-versa.
    const compartilhado = Math.max(grau.get(c.a) ?? 1, grau.get(c.b) ?? 1)
    const escala = compartilhado > 1 ? F.div(F.ONE, F.fromInt(compartilhado)) : F.ONE
    const j = F.mul(impulso.j, escala)
    const jt = F.mul(impulso.jt, escala)

    const nx = F.mul(j, impulso.normal.x) + F.mul(jt, impulso.tangente.x)
    const ny = F.mul(j, impulso.normal.y) + F.mul(jt, impulso.tangente.y)
    acumular(c.a, nx, ny)
    acumular(c.b, -nx, -ny)

    /*
     * O giro pago pelo throw.
     *
     * O atrito tangencial não some: ele frena o `spinZ` das duas bolas, que é o
     * que o produziu. Sem esta linha o efeito lateral desviaria bola após bola
     * sem nunca se gastar — energia saindo do nada.
     */
    if (jt !== 0) {
      const consumo = F.mul(F.abs(jt), F.from(2.5))
      c.a.spinZ -= c.a.spinZ > 0 ? Math.min(consumo, c.a.spinZ) : -Math.min(consumo, -c.a.spinZ)
      c.b.spinZ -= c.b.spinZ > 0 ? Math.min(consumo, c.b.spinZ) : -Math.min(consumo, -c.b.spinZ)
    }
    eventos.push({
      type: 'ball-ball',
      a: c.a.id,
      b: c.b.id,
      speed: impulso.velocidade,
      emA: V.clone(c.a.position),
      emB: V.clone(c.b.position),
    })
  }

  for (const [bola, delta] of somar) {
    bola.velocity.x += delta.x
    bola.velocity.y += delta.y
  }

  return eventos
}


/**
 * Roda até todas as bolas pararem.
 *
 * Devolve os eventos em ordem — é o que o cliente usa para som e animação, e o
 * que entra no hash do resultado.
 */
export function simulate(state: TableState, maxSteps = T.MAX_STEPS): CollisionEvent[] {
  return simularContando(state, maxSteps).events
}

/**
 * O mesmo `simulate`, dizendo também QUANTOS passos a mesa levou para parar.
 *
 * O servidor precisa disso para o relógio da vez: ele simula a tacada inteira
 * num instante, mas na tela as bolas levam `passos × DT` rolando — e o prazo
 * do próximo jogador corria durante esse tempo. É o mesmo laço, passo a passo:
 * contar não muda nada na física (o digest das fixtures é a prova).
 */
export function simularContando(
  state: TableState,
  maxSteps = T.MAX_STEPS,
): { events: CollisionEvent[]; passos: number } {
  const events: CollisionEvent[] = []
  let passos = 0
  for (; passos < maxSteps && isMoving(state); passos++) {
    events.push(...step(state))
  }
  return { events, passos }
}
